import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, sentinel } from '@sentinel/ui';

async function clearClipboard() {
  try {
    // Use a space (not '') because some browsers no-op on empty strings.
    await navigator.clipboard.writeText(' ');
  } catch {
    // ignore
  }
}

export interface RecoveryCodesStepProps {
  codes: string[];
  onDone: () => void;
  /**
   * Override the default header copy. Defaults to the enrollment-flow copy
   * ("Save your recovery codes" / setup-time subtitle / "Two-factor
   * authentication is now enabled"). The recovery-page reuses this component
   * to show server-regenerated codes and provides its own copy.
   */
  title?: string;
  subtitle?: string;
  /**
   * Status banner shown above the codes grid. Pass `null` to hide it
   * entirely (e.g. when the codes are auto-regenerated and the surrounding
   * page already shows a success state).
   */
  statusMessage?: string | null;
  /** Label for the "I've saved them" / continue button. */
  doneLabel?: string;
}

export function RecoveryCodesStep({
  codes,
  onDone,
  title,
  subtitle,
  statusMessage,
  doneLabel,
}: RecoveryCodesStepProps) {
  const { t } = useTranslation('mfa');
  const [copied, setCopied] = useState(false);

  const heading = title ?? t('recovery_title');
  const sub = subtitle ?? t('recovery_subtitle');
  const status = statusMessage === undefined ? t('enrollment_complete') : statusMessage;
  const done = doneLabel ?? t('recovery_done');

  async function onCopyAll() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        void clearClipboard();
      }, 2000);
    } catch {
      // ignore
    }
  }

  function handleDone() {
    void clearClipboard();
    onDone();
  }

  // Clear clipboard on unmount so recovery codes don't linger.
  useEffect(() => {
    return () => {
      void clearClipboard();
    };
  }, []);

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <h2 style={{ margin: 0 }}>{heading}</h2>
      <p>{sub}</p>

      {status ? (
        <Alert type="success" role="status">
          {status}
        </Alert>
      ) : null}

      <ul
        data-testid="mfa-recovery-codes"
        aria-label={heading}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '0.5rem',
          listStyle: 'none',
          padding: 0,
          margin: 0,
        }}
      >
        {codes.map((code, index) => (
          <li
            key={code}
            aria-label={t('recovery_code_n', { n: index + 1 })}
            style={{
              fontFamily: sentinel.fontMono,
              fontSize: '0.95rem',
              padding: '0.625rem 0.875rem',
              background: sentinel.surfaceOverlay,
              border: `1px solid ${sentinel.borderDefault}`,
              color: sentinel.textBody,
              borderRadius: 6,
              textAlign: 'center',
              userSelect: 'all',
            }}
          >
            {code}
          </li>
        ))}
      </ul>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <Button type="button" variant="secondary" onClick={onCopyAll}>
          {copied ? t('recovery_copied') : t('recovery_copy')}
        </Button>
        <Button type="button" data-testid="mfa-recovery-done" onClick={handleDone}>
          {done}
        </Button>
      </div>
    </div>
  );
}
